import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  #sidebarController;
  #sidebarDialog = null;
  #sidebarTrigger = null;
  #sidebarTimer;
  #sidebarHoverTimer;
  #sidebarLeaveTimer;
  #sidebarWasEntered = false;
  #sidebarHeaderObserver;
  #sidebarPositionFrame;
  #sidebarPositionUntil = 0;
  #savedOverflow = '';
  #sidebarGlassStyle;
  #sidebarGlassConfig = '';
  #sidebarClosing = false;
  #sidebarCloseListener;

  #trackSidebarPosition = () => {
    this.#sidebarPositionUntil = performance.now() + 500;
    if (!this.#sidebarPositionFrame) this.#sidebarPositionFrame = requestAnimationFrame(this.#positionSidebar);
  };

  #positionSidebar = () => {
    this.#sidebarPositionFrame = null;
    if (!this.#sidebarDialog?.open) return;
    const bottom = this.headerComponent?.getBoundingClientRect().bottom ?? 0;
    const top = Math.max(0, Math.min(innerHeight, bottom));
    this.#sidebarDialog.style.setProperty('--ora-sidebar-top', top + 'px');
    const width = this.#sidebarDialog.getBoundingClientRect().width;
    if (this.#sidebarGlassStyle) {
      const glassRule = `#${CSS.escape(this.#sidebarDialog.id)}::backdrop { inset: ${top}px auto 0 ${width}px; width: ${Math.max(0, (innerWidth - width) * (Number(this.dataset.glassWidth ?? 95) / 100))}px; --ora-glass-slide: ${width}px; ${this.#sidebarGlassConfig} }`;
      if (this.#sidebarGlassStyle.textContent !== glassRule) this.#sidebarGlassStyle.textContent = glassRule;
    }
    if (performance.now() < this.#sidebarPositionUntil) {
      this.#sidebarPositionFrame = requestAnimationFrame(this.#positionSidebar);
    }
  };

  #sidebarCleanup = (event) => {
    if (event && (event.target !== this.#sidebarDialog || event.target.open)) return;
    if (this.#sidebarCloseListener) this.#sidebarDialog?.removeEventListener('transitionend', this.#sidebarCloseListener);
    this.#sidebarCloseListener = null;
    this.#sidebarClosing = false;
    clearTimeout(this.#sidebarLeaveTimer);
    this.#sidebarGlassStyle?.remove();
    this.#sidebarGlassStyle = null;
    this.#sidebarHeaderObserver?.disconnect();
    cancelAnimationFrame(this.#sidebarPositionFrame);
    this.#sidebarPositionFrame = null;
    clearTimeout(this.#sidebarTimer);
    if (!this.#sidebarDialog) return;
    this.#sidebarTrigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = this.#savedOverflow;
    this.#sidebarDialog = null;
  };

  #closeSidebar = (immediate = false) => {
    clearTimeout(this.#sidebarHoverTimer);
    clearTimeout(this.#sidebarLeaveTimer);
    const dialog = this.#sidebarDialog;
    if (!dialog || (this.#sidebarClosing && !immediate)) return;
    this.#sidebarClosing = true;
    const wasVisible = dialog.dataset.visible === 'true';
    const finish = () => {
      if (this.#sidebarDialog !== dialog) return;
      dialog.dataset.visible = 'false';
      dialog.close();
      this.#sidebarCleanup();
    };
    if (immediate || !wasVisible || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }
    this.#sidebarCloseListener = (event) => {
      if (event.target === dialog && event.propertyName === 'transform' && !event.pseudoElement) finish();
    };
    dialog.addEventListener('transitionend', this.#sidebarCloseListener);
    dialog.dataset.visible = 'false';
    clearTimeout(this.#sidebarTimer);
    this.#sidebarTimer = setTimeout(finish, Number(this.dataset.sidebarDuration || 350) + 80);
  };

  #sidebarPointerOver = (event) => {
    if (this.dataset.oraSidebar !== 'true' || this.dataset.oraSidebarTrigger !== 'hover' ||
        innerWidth < 750 || event.pointerType === 'touch' || !matchMedia('(hover: hover)').matches) return;
    const trigger = event.target.closest('[data-ora-menu-index]');
    if (!trigger || trigger.contains(event.relatedTarget) || this.#sidebarDialog?.open) return;
    clearTimeout(this.#sidebarHoverTimer);
    this.#sidebarHoverTimer = setTimeout(() => {
      if (!trigger.isConnected || !trigger.matches(':hover')) return;
      this.#sidebarClick({ target: trigger, button: 0, preventDefault() {}, stopPropagation() {} });
    }, 180);
  };

  #sidebarPointerOut = (event) => {
    const trigger = event.target.closest('[data-ora-menu-index]');
    if (trigger && !trigger.contains(event.relatedTarget)) clearTimeout(this.#sidebarHoverTimer);
  };

  #sidebarMousePosition = (event) => {
    if (!this.#sidebarDialog || event.pointerType === 'touch') return;
    const rect = this.#sidebarDialog.getBoundingClientRect();
    const inside = event.clientX >= rect.left && event.clientX <= rect.right &&
      event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (inside) {
      this.#sidebarWasEntered = true;
      this.#sidebarEnter();
    } else if (this.#sidebarWasEntered && !this.#sidebarLeaveTimer) {
      this.#sidebarLeave(event);
    }
  };

  #sidebarLeave = (event) => {
    if (event.pointerType === 'touch' || !matchMedia('(hover: hover)').matches) return;
    clearTimeout(this.#sidebarLeaveTimer);
    this.#sidebarLeaveTimer = setTimeout(() => { this.#sidebarLeaveTimer = null; this.#closeSidebar(); }, 180);
  };

  #sidebarEnter = () => {
    clearTimeout(this.#sidebarLeaveTimer);
    this.#sidebarLeaveTimer = null;
  };

  scrollSidebarGallery = (event) => {
    const button = event.target.closest('[data-ora-gallery-step]');
    const track = button?.closest('.ora-menu-product')?.querySelector('.ora-menu-product__track');
    if (!track) return;
    const image = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = (image?.getBoundingClientRect().width || track.clientWidth) + gap;
    track.scrollBy({ left: distance * Number(button.dataset.oraGalleryStep),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };

  #sidebarCancel = (event) => {
    event.preventDefault();
    this.#closeSidebar();
  };

  #sidebarClick = (event) => {
    if (this.dataset.oraSidebar !== 'true' || innerWidth < 750) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (this.#sidebarDialog && (target.closest('[data-ora-close]') || target === this.#sidebarDialog)) {
      if (target === this.#sidebarDialog) {
        const rect = target.getBoundingClientRect();
        if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) return;
      }
      event.preventDefault();
      this.#closeSidebar();
      return;
    }
    const trigger = target.closest('[data-ora-menu-index]');
    if (!trigger || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const dialog = this.querySelector(`[data-ora-panel="${trigger.dataset.oraMenuIndex}"]`);
    if (!(dialog instanceof HTMLDialogElement)) return;
    event.preventDefault();
    event.stopPropagation();
    this.#closeSidebar(true);
    this.#sidebarTrigger = trigger;
    this.#sidebarDialog = dialog;
    this.#sidebarClosing = false;
    this.#sidebarWasEntered = false;
    this.#savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    trigger.setAttribute('aria-expanded', 'true');
    const headerBottom = this.headerComponent?.getBoundingClientRect().bottom ?? 0;
    dialog.style.setProperty('--ora-sidebar-top', Math.max(0, Math.min(innerHeight, headerBottom)) + 'px');
    const enabled = this.dataset.glassEnabled !== 'false';
    const grain = Math.max(0, Math.min(40, Number(this.dataset.glassGrain ?? 12))) / 100;
    const noise = "<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/><feComponentTransfer><feFuncA type='linear' slope='" + grain + "'/></feComponentTransfer></filter><rect width='100%' height='100%' filter='url(#n)'/></svg>";
    const tint = this.dataset.glassColor || '#ffffff';
    const opacity = Math.max(0, Math.min(100, Number(this.dataset.glassOpacity ?? 20)));
    this.#sidebarGlassConfig = `
      --ora-glass-enabled: ${enabled ? 1 : 0};
      --ora-glass-blur: ${enabled ? Number(this.dataset.glassBlur ?? 90) : 0}px;
      --ora-glass-saturation: ${Number(this.dataset.glassSaturation ?? 125)}%;
      --ora-glass-fade: ${Number(this.dataset.glassFade ?? 45)}%;
      --ora-sidebar-duration: ${Number(this.dataset.sidebarDuration || 350)}ms;
      --ora-glass-tint: color-mix(in srgb, ${tint} ${opacity}%, transparent);
      --ora-glass-noise: ${grain > 0 && enabled ? 'url("data:image/svg+xml,' + encodeURIComponent(noise) + '")' : 'none'};
    `;
    this.#sidebarGlassStyle = document.createElement('style');
    document.head.appendChild(this.#sidebarGlassStyle);
    dialog.showModal();
    dialog.focus({ preventScroll: true });
    this.#positionSidebar();
    this.#sidebarHeaderObserver = new ResizeObserver(this.#trackSidebarPosition);
    if (this.headerComponent) this.#sidebarHeaderObserver.observe(this.headerComponent);
    this.#trackSidebarPosition();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (dialog.open && this.#sidebarDialog === dialog && !this.#sidebarClosing) dialog.dataset.visible = 'true';
    }));
  };

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#sidebarController = new AbortController();
    this.addEventListener('click', this.#sidebarClick, { capture: true, signal: this.#sidebarController.signal });
    this.addEventListener('pointerover', this.#sidebarPointerOver, { signal: this.#sidebarController.signal });
    this.addEventListener('pointerout', this.#sidebarPointerOut, { signal: this.#sidebarController.signal });
    this.querySelectorAll('.ora-desktop-menu').forEach((dialog) => {
      dialog.addEventListener('pointermove', this.#sidebarMousePosition, { signal: this.#sidebarController.signal });
      dialog.addEventListener('pointerleave', this.#sidebarLeave, { signal: this.#sidebarController.signal });
      dialog.addEventListener('pointerenter', this.#sidebarEnter, { signal: this.#sidebarController.signal });
      dialog.addEventListener('cancel', this.#sidebarCancel, { signal: this.#sidebarController.signal });
      dialog.addEventListener('close', this.#sidebarCleanup, { signal: this.#sidebarController.signal });
    });
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    this.#closeSidebar(true);
    this.#sidebarController?.abort();
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#closeSidebar(true);
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.dataset.oraSidebar === 'true' && innerWidth >= 750 && event.target.closest('[data-ora-menu-index], .menu-list__list-item:not([slot="more"])')) return;
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => { if (!image.closest('.ora-desktop-menu')) image.removeAttribute('loading'); });
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
